import React, { useEffect, useState } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { DebtCrushPlan, ProjectionDataPoint } from '../types';
import { CheckCircle, Target, TrendingDown, Landmark, Sparkles } from 'lucide-react';

interface DebtCrushProps {
  userId: string;
  householdId: string;
}

type StrategyType = 'snowball' | 'avalanche' | 'cashflowdam' | 'snowball_cfd' | 'avalanche_cfd';

export default function DebtCrush({ userId, householdId }: DebtCrushProps) {
  const [plan, setPlan] = useState<DebtCrushPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [strategy, setStrategy] = useState<StrategyType>('snowball');
  const [completedPayments, setCompletedPayments] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const docRef = doc(db, 'debt_crush_plans', householdId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data() as DebtCrushPlan;
          setPlan(data);
          setCompletedPayments(new Set(data.completedPayments || []));
        }
      } catch (err) {
        console.error('Error fetching Debt Crush plan:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [householdId]);

  const togglePayment = async (month: string, debtName: string) => {
    const key = `${month}_${debtName}`;
    const newCompleted = new Set(completedPayments);
    
    if (newCompleted.has(key)) {
      newCompleted.delete(key);
    } else {
      newCompleted.add(key);
    }
    
    setCompletedPayments(newCompleted);
    
    try {
      setSaving(true);
      await updateDoc(doc(db, 'debt_crush_plans', householdId), {
        completedPayments: Array.from(newCompleted)
      });
    } catch (err) {
      console.error('Error updating payment status:', err);
      // Revert on failure
      if (completedPayments.has(key)) {
        newCompleted.add(key);
      } else {
        newCompleted.delete(key);
      }
      setCompletedPayments(newCompleted);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex-1 overflow-y-auto p-6 bg-slate-50 flex flex-col items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 text-center max-w-md">
          <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">No Active Strategy Found</h2>
          <p className="text-slate-500 mb-6">
            To unlock the Debt Crush Schedule, you first need to run the AI Advisor to mathematically generate your optimal path to zero debt.
          </p>
          <p className="text-sm text-indigo-600 font-semibold">
            Head over to the 'Debt & Wealth Advisor' tab and click Generate Full Strategy!
          </p>
        </div>
      </div>
    );
  }

  const scheduleData = plan.projections[strategy] || [];
  
  // Filter out months where no payments are being made AND there is no deficit
  const activeMonths = scheduleData.filter(m => (m.payments && Object.keys(m.payments).length > 0) || (m.deficit && m.deficit > 0));
  
  // Get all unique debt names across all months for this strategy
  const uniqueDebts = new Set<string>();
  activeMonths.forEach(m => {
    if (m.payments) {
      Object.keys(m.payments).forEach(d => uniqueDebts.add(d));
    }
  });
  const debtColumns = Array.from(uniqueDebts).sort();

  return (
    <div className="flex flex-col h-full overflow-hidden bg-slate-50">
      <div className="p-6 bg-white border-b border-slate-200 shrink-0">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Debt Crush Schedule</h2>
            <p className="text-slate-500 mt-1">Your exact month-by-month mathematical payment plan.</p>
          </div>
          {saving && <span className="text-sm text-slate-400">Saving progress...</span>}
        </div>

        <div className="flex flex-wrap gap-2 bg-slate-100 p-1 rounded-xl max-w-3xl">
          <button
            onClick={() => setStrategy('snowball')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${strategy === 'snowball' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Target className="w-4 h-4" /> Snowball
          </button>
          <button
            onClick={() => setStrategy('avalanche')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${strategy === 'avalanche' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <TrendingDown className="w-4 h-4" /> Avalanche
          </button>
          <button
            onClick={() => setStrategy('cashflowdam')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${strategy === 'cashflowdam' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Landmark className="w-4 h-4" /> CFD
          </button>
          <button
            onClick={() => setStrategy('snowball_cfd')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${strategy === 'snowball_cfd' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <Target className="w-4 h-4" /> Snowball + CFD
          </button>
          <button
            onClick={() => setStrategy('avalanche_cfd')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${strategy === 'avalanche_cfd' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            <TrendingDown className="w-4 h-4" /> Avalanche + CFD
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-sm font-semibold text-slate-900 w-32 whitespace-nowrap">Month</th>
                  {debtColumns.map(debt => (
                    <th key={debt} className="px-6 py-4 text-sm font-semibold text-slate-900 min-w-[200px]">{debt}</th>
                  ))}
                  <th className="px-6 py-4 text-sm font-semibold text-slate-900">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeMonths.map((row, idx) => {
                  const dateStr = new Date(row.month + '-02').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                  const hasDeficit = row.deficit && row.deficit > 0;
                  
                  return (
                    <tr key={row.month} className={`transition-colors ${hasDeficit ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`font-medium ${hasDeficit ? 'text-red-700' : 'text-slate-900'}`}>{dateStr}</span>
                      </td>
                      {debtColumns.map(debtName => {
                        const amountPaid = row.payments?.[debtName];
                        if (!amountPaid || amountPaid <= 0) {
                          return <td key={debtName} className="px-6 py-4 text-slate-300">-</td>;
                        }
                        
                        const paymentKey = `${row.month}_${debtName}`;
                        const isCompleted = completedPayments.has(paymentKey);
                        
                        return (
                          <td key={debtName} className="px-6 py-4">
                            <button 
                              onClick={() => togglePayment(row.month, debtName)}
                              className={`flex items-center gap-2 group transition-all w-full text-left p-2 -m-2 rounded-lg hover:bg-white hover:shadow-sm border border-transparent ${isCompleted ? 'hover:border-green-200' : 'hover:border-slate-200'}`}
                            >
                              <div className={`shrink-0 rounded-full flex items-center justify-center transition-colors ${isCompleted ? 'text-emerald-500' : 'text-slate-200 group-hover:text-indigo-400'}`}>
                                <CheckCircle className="w-5 h-5" />
                              </div>
                              <span className={`font-semibold transition-all ${isCompleted ? 'text-slate-400 line-through' : 'text-slate-700 group-hover:text-slate-900'}`}>
                                ${amountPaid.toFixed(2)}
                              </span>
                            </button>
                          </td>
                        );
                      })}
                      <td className="px-6 py-4 text-sm">
                        {hasDeficit ? (
                          <div className="flex items-center gap-2 text-red-600 font-semibold">
                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                            Short by ${row.deficit.toFixed(2)} (Read Advisor Report)
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {activeMonths.length === 0 && (
                  <tr>
                    <td colSpan={debtColumns.length + 2} className="px-6 py-12 text-center text-slate-500">
                      No payments found in this projection. Your debts might already be zero!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
